import React, { FunctionComponent, useState } from "react";
import { UseIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormLabel, Header6, styled } from "@iventis/styles";
import { Autocomplete, Accordion, AccordionSummary, AccordionDetails, TextField, Tabs, Tab } from "@mui/material";
import { MapObjectExportCadUnits } from "@iventis/domain-model/model/mapObjectExportCadUnits";
import { MapObjectExportCadOptions } from "@iventis/domain-model/model/mapObjectExportCadOptions";
import { Content } from "@iventis/translations";
import { FormWizardTitle } from "./form-wizard-title";
import { FormWizardTemplate, Stage } from "./form-wizard-template";
import { CustomDialog } from "./custom-dialog";
import { NumberInputComponent } from "./number-input";
import { EPSG_DEFS } from "./constants/epsg-defs";

export const ExportDwgDialog: FunctionComponent<{
    onClose?: () => void;
    onConfirm: (exportDwgOptions: MapObjectExportCadOptions) => void;
    open: boolean;
    translate?: UseIventisTranslate;
}> = ({ onClose, onConfirm, open, translate }) => {
    const [tab, setTab] = useState(0);

    const onExportClicked = () => {
        // if drawing units, do not return epsg code
        if (tab === 0) {
            if (
                cadOptions.coordinateRebase.sourceX === 0 &&
                cadOptions.coordinateRebase.sourceY === 0 &&
                cadOptions.coordinateRebase.targetX === 0 &&
                cadOptions.coordinateRebase.targetY === 0 &&
                cadOptions.coordinateRebase.targetRotationDegrees === 0
            ) {
                // if no rebase options set, do not return rebase
                return onConfirm({ ...cadOptions, targetEpsgCode: null, coordinateRebase: null });
            }

            return onConfirm({ ...cadOptions, targetEpsgCode: null });
        }
        // if coordinate system, do not return units or rebase
        return onConfirm({ ...cadOptions, units: null, coordinateRebase: null });
    };

    const [cadOptions, setCadOptions] = useState<MapObjectExportCadOptions>({
        targetEpsgCode: 27700,
        coordinateRebase: {
            targetX: 0,
            targetY: 0,
            targetRotationDegrees: 0,
            sourceX: 0,
            sourceY: 0,
        },
        units: MapObjectExportCadUnits.Metres,
    });

    const stages: Stage[] = [
        {
            secondaryButtons: [{ buttonText: translate(Content.common.buttons.cancel), onButtonPressed: onClose }],
            primaryButtonText: translate(Content.common.buttons.export),
            primaryButtonCallback: () => onExportClicked(),
            isValid: true,
            Component: (
                <>
                    <Tabs variant="fullWidth" indicatorColor="primary" value={tab} onChange={(_, newValue) => setTab(newValue)}>
                        <Tab label={translate(Content.map11.drawing_units)} />
                        <Tab label={translate(Content.map11.coordinate_system)} />
                    </Tabs>
                    {tab === 0 && (
                        <StyledContainer>
                            <StyledRow>
                                <StyledNumberField>
                                    <Autocomplete
                                        className="unit-selector"
                                        value={cadOptions.units}
                                        options={Object.values(MapObjectExportCadUnits)}
                                        onChange={(_, value) => setCadOptions((prev) => ({ ...prev, units: value }))}
                                        disableClearable
                                        sx={{ width: "100%" }}
                                        renderInput={(params) => (
                                            <TextField
                                                // eslint-disable-next-line react/jsx-props-no-spreading
                                                {...params}
                                            />
                                        )}
                                    />
                                </StyledNumberField>
                            </StyledRow>
                            <Accordion defaultExpanded={false} disableGutters sx={{ mt: 2, boxShadow: "none", background: "none", border: 0 }}>
                                <AccordionSummary
                                    expandIcon={<FontAwesomeIcon icon={["fas", "chevron-down"]} />}
                                    sx={{ px: 0, pt: 1, minHeight: 0, "& .MuiAccordionSummary-content": { m: 0 } }}
                                >
                                    <Header6>{translate(Content.map11.advanced_rebase_options)}</Header6>
                                </AccordionSummary>
                                <StyledAccordionDetails>
                                    <StyledRow>
                                        <StyledNumberField>
                                            <FormLabel>{translate(Content.map11.source_longitude)}</FormLabel>
                                            <NumberInputComponent
                                                value={0}
                                                onValidValueInputted={(value) =>
                                                    setCadOptions((prev) => ({
                                                        ...prev,
                                                        coordinateRebase: { ...prev.coordinateRebase, sourceX: value },
                                                    }))
                                                }
                                                testId="source-lng-input"
                                                maxValue={180}
                                                minValue={-180}
                                            />
                                        </StyledNumberField>
                                        <StyledNumberField>
                                            <FormLabel>{translate(Content.map11.source_latitude)}</FormLabel>
                                            <NumberInputComponent
                                                value={cadOptions.coordinateRebase.sourceY}
                                                onValidValueInputted={(value) =>
                                                    setCadOptions((prev) => ({
                                                        ...prev,
                                                        coordinateRebase: { ...prev.coordinateRebase, sourceY: value },
                                                    }))
                                                }
                                                testId="source-lat-input"
                                                maxValue={90}
                                                minValue={-90}
                                            />
                                        </StyledNumberField>
                                    </StyledRow>
                                    <StyledRow>
                                        <StyledNumberField>
                                            <FormLabel>{translate(Content.map11.target_x)}</FormLabel>
                                            <NumberInputComponent
                                                value={0}
                                                onValidValueInputted={(value) =>
                                                    setCadOptions((prev) => ({
                                                        ...prev,
                                                        coordinateRebase: { ...prev.coordinateRebase, targetX: value },
                                                    }))
                                                }
                                                testId="target-x-input"
                                            />
                                        </StyledNumberField>
                                        <StyledNumberField>
                                            <FormLabel>{translate(Content.map11.target_y)}</FormLabel>
                                            <NumberInputComponent
                                                value={0}
                                                onValidValueInputted={(value) =>
                                                    setCadOptions((prev) => ({
                                                        ...prev,
                                                        coordinateRebase: { ...prev.coordinateRebase, targetY: value },
                                                    }))
                                                }
                                                testId="target-y-input"
                                            />
                                        </StyledNumberField>
                                    </StyledRow>
                                    <StyledRow>
                                        <StyledNumberField>
                                            <FormLabel>{translate(Content.map11.target_rotation)}</FormLabel>
                                            <NumberInputComponent
                                                value={0}
                                                onValidValueInputted={(value) =>
                                                    setCadOptions((prev) => ({
                                                        ...prev,
                                                        coordinateRebase: { ...prev.coordinateRebase, targetRotationDegrees: value },
                                                    }))
                                                }
                                                testId="rotation-input"
                                                maxValue={360}
                                                minValue={0}
                                            />
                                        </StyledNumberField>
                                    </StyledRow>
                                </StyledAccordionDetails>
                            </Accordion>
                        </StyledContainer>
                    )}
                    {tab === 1 && (
                        <StyledContainer>
                            <StyledNumberField>
                                <Autocomplete
                                    className="coordinate-selector"
                                    options={EPSG_DEFS}
                                    value={EPSG_DEFS.find((def) => def.code === cadOptions.targetEpsgCode) || null}
                                    getOptionLabel={(opt) => `${opt.name} - ${opt.code}`}
                                    isOptionEqualToValue={(option, value) => option.code === value.code}
                                    onChange={(_, value) => setCadOptions((prev) => ({ ...prev, targetEpsgCode: value?.code }))}
                                    disableClearable
                                    sx={{ width: "100%" }}
                                    renderInput={(params) => (
                                        <TextField
                                            // eslint-disable-next-line react/jsx-props-no-spreading
                                            {...params}
                                        />
                                    )}
                                />
                            </StyledNumberField>
                        </StyledContainer>
                    )}
                </>
            ),
        },
    ];
    return (
        <CustomDialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
            <FormWizardTemplate
                modalTestId="dwg-exports-modal"
                title={
                    <FormWizardTitle
                        title={
                            <StyledTitle>
                                <FontAwesomeIcon icon={["far", "compass-drafting"]} />
                                {translate(Content.map11.export_dwg)}
                            </StyledTitle>
                        }
                    />
                }
                currentStage={0}
                stages={stages}
            />
        </CustomDialog>
    );
};

const StyledContainer = styled.div`
    padding-top: 16px;
    gap: 8px;
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const StyledRow = styled.div`
    display: flex;
    flex-direction: row;
    gap: 16px;
`;

const StyledNumberField = styled.div`
    display: flex;
    flex-direction: column;
    flex: 0 0 calc(50% - 8px);
    gap: 4px;
`;

const StyledTitle = styled.div`
    display: flex;
    gap: 5px;
    align-items: center;
`;

const StyledAccordionDetails = styled(AccordionDetails)`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 8px 0 0 0; /* keep top padding, remove side paddings */
`;
